import { useCallback, useEffect, useMemo, useState } from 'react';
import { EditRequestForm } from '@/components/EditRequestForm';
import { StudioDetailHeader } from '@/components/StudioDetailHeader';
import { ImageLightbox, type LightboxImage } from '@/components/ui/ImageLightbox';
import { useDrawerCloseGuard } from '@/hooks/useDrawerCloseGuard';
import type { Studio } from '@/types';

interface StudioDetailViewProps {
  studio: Studio;
  titleId: string;
  onRequestClose: () => void;
  onClose: () => void;
  registerCloseHandler?: (handler: (() => void) | null) => void;
  aiIdPrefix?: string;
}

export function StudioDetailView({
  studio,
  titleId,
  onRequestClose,
  onClose,
  registerCloseHandler,
  aiIdPrefix = 'studio-detail-view',
}: StudioDetailViewProps) {
  const [isEditRequestOpen, setIsEditRequestOpen] = useState(false);
  const [hasUnsavedEditRequest, setHasUnsavedEditRequest] = useState(false);
  const [routeLightboxIndex, setRouteLightboxIndex] = useState<number | null>(null);
  const routeImages = studio.routeImages.length > 0 ? studio.routeImages : studio.routeImage ? [studio.routeImage] : [];
  const routeLightboxImages = useMemo<LightboxImage[]>(
    () =>
      routeImages.map((routeImage, index) => ({
        src: routeImage.url,
        alt: routeImage.alt || `${studio.title} 가는길 이미지 ${index + 1}`,
      })),
    [routeImages, studio.title],
  );

  useEffect(() => {
    setIsEditRequestOpen(false);
    setHasUnsavedEditRequest(false);
    setRouteLightboxIndex(null);
  }, [studio.id]);

  const handleGuardedClose = useDrawerCloseGuard({
    hasUnsavedChanges: isEditRequestOpen && hasUnsavedEditRequest,
    onClose,
  });

  useEffect(() => {
    registerCloseHandler?.(handleGuardedClose);
    return () => registerCloseHandler?.(null);
  }, [handleGuardedClose, registerCloseHandler]);

  const handleCancelEditRequest = useCallback(() => {
    if (!hasUnsavedEditRequest || window.confirm('작성 중인 수정요청이 사라져요. 닫을까요?')) {
      setIsEditRequestOpen(false);
      setHasUnsavedEditRequest(false);
    }
  }, [hasUnsavedEditRequest]);

  return (
    <div className="flex h-full min-h-0 flex-col" data-ai-id={aiIdPrefix}>
      <StudioDetailHeader studio={studio} titleId={titleId} onClose={onRequestClose} aiIdPrefix={`${aiIdPrefix}-header`} />

      <div className="flex-1 overflow-y-auto px-5 py-5" data-ai-id={`${aiIdPrefix}-content`}>
        <div className="space-y-5" data-ai-id={`${aiIdPrefix}-sections`}>
          <section className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4" data-ai-id={`${aiIdPrefix}-location-card`}>
            <h3 className="text-sm font-semibold text-slate-950" data-ai-id={`${aiIdPrefix}-location-card-title`}>
              <span data-ai-id={`${aiIdPrefix}-location-card-title-text`}>위치</span>
            </h3>
            <p className="mt-2 text-base leading-7 text-slate-700" data-ai-id={`${aiIdPrefix}-location-card-value`}>
              <span data-ai-id={`${aiIdPrefix}-location-card-value-text`}>{studio.location || '위치 미입력'}</span>
            </p>
          </section>

          {routeImages.length > 0 ? (
            <section className="rounded-2xl border border-slate-200 bg-white px-4 py-4" data-ai-id={`${aiIdPrefix}-route-images-card`}>
              <h3 className="text-sm font-semibold text-slate-950" data-ai-id={`${aiIdPrefix}-route-images-card-title`}>
                <span data-ai-id={`${aiIdPrefix}-route-images-card-title-text`}>가는길 이미지</span>
              </h3>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2" data-ai-id={`${aiIdPrefix}-route-images-card-gallery`}>
                {routeImages.map((routeImage, index) => (
                  <figure
                    key={routeImage.id}
                    className="group overflow-hidden rounded-xl border border-slate-100 bg-slate-50"
                    data-ai-id={`${aiIdPrefix}-route-images-card-gallery-item`}
                  >
                    <button
                      type="button"
                      onClick={() => setRouteLightboxIndex(index)}
                      className="block w-full cursor-zoom-in overflow-hidden text-left focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-100"
                      aria-label={`${studio.title} 가는길 이미지 ${index + 1} 크게 보기`}
                      data-ai-id={`${aiIdPrefix}-route-images-card-gallery-item-button`}
                    >
                      <img
                        src={routeImage.url}
                        alt={routeImage.alt || `${studio.title} 가는길 이미지 ${index + 1}`}
                        width={routeImage.width ?? undefined}
                        height={routeImage.height ?? undefined}
                        loading="lazy"
                        className="h-auto w-full object-cover transition group-hover:scale-[1.02]"
                        data-ai-id={`${aiIdPrefix}-route-images-card-gallery-item-image`}
                      />
                    </button>
                  </figure>
                ))}
              </div>
              <p className="mt-3 text-sm text-slate-500" data-ai-id={`${aiIdPrefix}-route-images-card-caption`}>
                <span data-ai-id={`${aiIdPrefix}-route-images-card-caption-text`}>지도로 찾기 어려울 때 참고하는 가는길 이미지입니다.</span>
              </p>
            </section>
          ) : null}

          {studio.spaceCloudUrl ? (
            <a
              href={studio.spaceCloudUrl}
              target="_blank"
              rel="noreferrer"
              className="flex h-12 items-center justify-center rounded-2xl bg-slate-950 px-4 text-sm font-semibold text-white transition hover:bg-slate-800"
              data-ai-id={`${aiIdPrefix}-spacecloud-link`}
            >
              <span data-ai-id={`${aiIdPrefix}-spacecloud-link-label-text`}>스페이스클라우드 열기</span>
            </a>
          ) : null}

          <section data-ai-id={`${aiIdPrefix}-edit-request-card`}>
            {!isEditRequestOpen ? (
              <button
                type="button"
                onClick={() => setIsEditRequestOpen(true)}
                className="inline-flex h-11 w-full items-center justify-center rounded-xl bg-emerald-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-100"
                data-ai-id={`${aiIdPrefix}-edit-request-card-open-button`}
              >
                <span data-ai-id={`${aiIdPrefix}-edit-request-card-open-button-label-text`}>수정요청</span>
              </button>
            ) : null}

            {isEditRequestOpen ? (
              <div className="mt-4" data-ai-id={`${aiIdPrefix}-edit-request-card-form-wrapper`}>
                <EditRequestForm
                  studioId={studio.id}
                  studioTitle={studio.title}
                  onDirtyChange={setHasUnsavedEditRequest}
                  onCancel={handleCancelEditRequest}
                  aiIdPrefix={`${aiIdPrefix}-edit-request-card-form`}
                />
              </div>
            ) : null}
          </section>

        </div>
      </div>

      <ImageLightbox
        images={routeLightboxImages}
        currentIndex={routeLightboxIndex}
        onClose={() => setRouteLightboxIndex(null)}
        onIndexChange={setRouteLightboxIndex}
        aiIdPrefix={`${aiIdPrefix}-route-images-lightbox`}
      />
    </div>
  );
}
